import React from 'react';
import { useAuth } from '../AuthContext';
import { Wrench } from 'lucide-react';

/**
 * Fullscreen maintenance gate — clean centered card, AqmOpti branded.
 * Discrete admin access at the bottom of the card.
 */
export default function MaintenanceScreen() {
  const { loginWithDiscord, isAuthenticated, logout, user } = useAuth();

  return (
    <div
      className="fixed inset-0 z-[9999] bg-[#080a12] flex items-center justify-center px-4 overflow-hidden"
      data-testid="maintenance-screen"
    >
      {/* Subtle ambient glows — top-left cyan, bottom-right cyan */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-32 -left-24 w-[520px] h-[520px] bg-cyan-500/[0.08] rounded-full blur-3xl" />
        <div className="absolute -bottom-32 -right-24 w-[480px] h-[480px] bg-cyan-400/[0.05] rounded-full blur-3xl" />
      </div>

      {/* Card */}
      <div className="relative z-10 w-full max-w-[440px]">
        <div className="bg-[#0f1119]/90 backdrop-blur-sm border border-white/[0.06] rounded-[20px] px-8 py-11 md:px-10 md:py-12 shadow-[0_25px_80px_-15px_rgba(0,0,0,0.7)]">
          {/* Brand row */}
          <div className="flex items-center justify-center gap-2.5 mb-9">
            <img
              src="/aqmopti-logo.png"
              alt="AqmOpti"
              className="h-7 w-7 object-contain"
              style={{ mixBlendMode: 'lighten' }}
            />
            <span className="text-white font-bold tracking-wide text-base">AqmOpti</span>
          </div>

          {/* Icon badge — simple, sober */}
          <div className="flex justify-center mb-6">
            <div className="w-[54px] h-[54px] rounded-xl bg-[#151823] border border-cyan-500/25 flex items-center justify-center">
              <Wrench className="w-6 h-6 text-cyan-400" strokeWidth={2} />
            </div>
          </div>

          {/* Title */}
          <h1
            className="text-center text-[26px] md:text-[28px] leading-tight font-bold text-white mb-3"
            data-testid="maintenance-title"
          >
            Lancement imminent
          </h1>

          {/* Description */}
          <p className="text-center text-gray-400 text-[13.5px] leading-[1.65] mb-6">
            Le site est actuellement en maintenance.
            <br />
            Le service sera de nouveau accessible sous peu.
          </p>

          {/* Discord order notice */}
          <div className="rounded-xl bg-[#5865F2]/10 border border-[#5865F2]/25 px-4 py-3 mb-8">
            <p className="text-center text-[12.5px] text-gray-300 leading-relaxed">
              Pour passer commande pendant la maintenance,
              <br />
              rejoins-nous sur <a
                href="https://discord.gg/aqmopti"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#a5b0ff] hover:text-white font-semibold underline underline-offset-2"
                data-testid="maintenance-discord-link"
              >Discord</a>.
            </p>
          </div>

          {/* Discrete divider */}
          <div className="h-px bg-gradient-to-r from-transparent via-white/[0.07] to-transparent mb-6" />

          {/* Admin access */}
          {isAuthenticated && !user?.is_admin ? (
            <div className="text-center">
              <p className="text-[12px] text-red-400/80 mb-2" data-testid="maintenance-not-admin">
                Ton compte n'a pas accès pour le moment.
              </p>
              <button
                onClick={logout}
                className="text-[11px] text-gray-500 hover:text-gray-300 underline underline-offset-2 transition-colors"
                data-testid="maintenance-logout-btn"
              >
                Se déconnecter
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-1.5 text-[12.5px]">
              <span className="text-gray-500">Espace administrateur</span>
              <button
                onClick={loginWithDiscord}
                className="text-cyan-400 hover:text-cyan-300 font-medium transition-colors inline-flex items-center gap-0.5"
                data-testid="maintenance-login-btn"
              >
                Accéder<span aria-hidden className="ml-0.5">→</span>
              </button>
            </div>
          )}
        </div>

        {/* Status footer outside card */}
        <div className="flex items-center justify-center gap-2 mt-5 text-[11px] uppercase tracking-[0.15em] text-gray-500">
          <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 shadow-[0_0_8px_rgba(6,182,212,0.8)]" />
          Retour très bientôt
        </div>
      </div>
    </div>
  );
}
